import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import axiosInstance from '@/utils/axios';
import { parseErrorMessage } from '@/utils/errorHandler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';

const DEPARTMENTS = [
  'Cundinamarca',
  'Antioquia',
  'Valle del Cauca',
  'Bolívar',
  'Santander',
  'Atlántico',
  'Magdalena',
  'Caldas',
  'Risaralda',
  'Quindío',
  'Meta',
];

const CITIES = {
  Cundinamarca: [
    'Bogotá',
    'Soacha',
    'Fusagasugá',
    'Chía',
    'Zipaquirá',
    'Girardot',
  ],
  Antioquia: [
    'Medellín',
    'Envigado',
    'Rionegro',
    'Itagüí',
    'Bello',
    'Sabaneta',
  ],
  'Valle del Cauca': ['Cali', 'Palmira', 'Buenaventura', 'Tuluá', 'Cartago'],
  Bolívar: ['Cartagena', 'Turbaco', 'Magangué'],
  Santander: ['Bucaramanga', 'Floridablanca', 'Girón', 'Piedecuesta'],
  Atlántico: ['Barranquilla', 'Soledad', 'Malambo'],
  Magdalena: ['Santa Marta', 'Ciénaga'],
  Caldas: ['Manizales', 'La Dorada'],
  Risaralda: ['Pereira', 'Dosquebradas'],
  Quindío: ['Armenia', 'Calarcá'],
  Meta: ['Villavicencio', 'Acacías'],
};

const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Apartamento' },
  { value: 'HOUSE', label: 'Casa' },
  { value: 'LOT', label: 'Lote' },
  { value: 'FARM', label: 'Finca' },
  { value: 'OFFICE', label: 'Oficina' },
  { value: 'LOCAL', label: 'Local Comercial' },
];

const PublishPropertyPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    operation: '',
    property_type: '',
    price: '',
    area_m2: '',
    rooms: '',
    bathrooms: '',
    parking: '',
    department: '',
    city: '',
    neighborhood: '',
    address_text: '',
  });
  const [images, setImages] = useState([]);

  if (!isAuthenticated) {
    return (
      <div className='min-h-screen bg-slate-50 py-16 flex items-center justify-center'>
        <div className='text-center'>
          <h2 className='text-2xl font-heading font-bold text-slate-900 mb-4'>
            Debes iniciar sesión
          </h2>
          <p className='text-slate-600 mb-6'>
            Para publicar una propiedad necesitas una cuenta
          </p>
          <Button
            onClick={() => navigate('/login')}
            className='bg-primary hover:bg-primary-hover'
          >
            Iniciar Sesión
          </Button>
        </div>
      </div>
    );
  }

  const handleDepartmentChange = (value) => {
    setFormData({
      ...formData,
      department: value,
      city: '', // Reset city when department changes
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (images.length === 0) {
      toast.error('Debes subir al menos una imagen');
      return;
    }

    setLoading(true);

    try {
      // Prepare data
      const propertyData = {
        ...formData,
        price: parseFloat(formData.price),
        area_m2: parseFloat(formData.area_m2),
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        parking: formData.parking ? parseInt(formData.parking) : null,
        images: images,
      };

      const { data } = await axiosInstance.post('/properties', propertyData);
      toast.success('¡Propiedad creada! Ahora debes pagar para publicarla.');

      // Redirect to payment page with the new property ID
      navigate(`/pagos?property=${data.id}&plan=publish`);
    } catch (error) {
      console.error('Publish error:', error);
      const errorMessage = parseErrorMessage(
        error,
        'Error al publicar la propiedad'
      );
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const availableCities = formData.department
    ? CITIES[formData.department] || []
    : [];

  return (
    <div className='min-h-screen bg-slate-50 py-12'>
      <div className='max-w-3xl mx-auto px-4 md:px-8'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className='bg-white rounded-2xl shadow-xl p-8 border border-slate-200'>
            <h1 className='text-3xl font-heading font-bold text-slate-900 mb-2'>
              Publicar Propiedad
            </h1>
            <p className='text-slate-600 mb-8'>
              Completa la información de tu propiedad
            </p>

            <form
              onSubmit={handleSubmit}
              className='space-y-6'
              data-testid='publish-form'
            >
              {/* Basic Info */}
              <div className='space-y-4'>
                <h2 className='text-xl font-heading font-semibold text-slate-900'>
                  Información Básica
                </h2>

                <div>
                  <Label htmlFor='title'>Título *</Label>
                  <Input
                    id='title'
                    required
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder='Ej: Apartamento Moderno en Chapinero'
                    className='mt-2'
                    data-testid='title-input'
                  />
                </div>

                <div>
                  <Label htmlFor='description'>Descripción *</Label>
                  <Textarea
                    id='description'
                    required
                    rows={4}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder='Describe tu propiedad en detalle...'
                    className='mt-2'
                    data-testid='description-input'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='operation'>Operación *</Label>
                    <Select
                      value={formData.operation}
                      onValueChange={(value) =>
                        setFormData({ ...formData, operation: value })
                      }
                    >
                      <SelectTrigger
                        id='operation'
                        className='mt-2'
                        data-testid='operation-select'
                      >
                        <SelectValue placeholder='Seleccionar' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='SALE'>Venta</SelectItem>
                        <SelectItem value='RENT'>Arriendo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor='property_type'>Tipo de Propiedad *</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, property_type: value })
                      }
                    >
                      <SelectTrigger
                        id='property_type'
                        className='mt-2'
                        data-testid='property-type-select'
                      >
                        <SelectValue placeholder='Seleccionar' />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='price'>Precio (COP) *</Label>
                    <Input
                      id='price'
                      type='number'
                      required
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder='5000000'
                      className='mt-2'
                      data-testid='price-input'
                    />
                  </div>

                  <div>
                    <Label htmlFor='area_m2'>Área (m²) *</Label>
                    <Input
                      id='area_m2'
                      type='number'
                      required
                      value={formData.area_m2}
                      onChange={(e) =>
                        setFormData({ ...formData, area_m2: e.target.value })
                      }
                      placeholder='85'
                      className='mt-2'
                      data-testid='area-input'
                    />
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className='space-y-4 pt-6 border-t border-slate-200'>
                <h2 className='text-xl font-heading font-semibold text-slate-900'>
                  Características
                </h2>

                <div className='grid grid-cols-3 gap-4'>
                  <div>
                    <Label htmlFor='rooms'>Habitaciones</Label>
                    <Input
                      id='rooms'
                      type='number'
                      min='0'
                      value={formData.rooms}
                      onChange={(e) =>
                        setFormData({ ...formData, rooms: e.target.value })
                      }
                      placeholder='2'
                      className='mt-2'
                      data-testid='rooms-input'
                    />
                  </div>

                  <div>
                    <Label htmlFor='bathrooms'>Baños</Label>
                    <Input
                      id='bathrooms'
                      type='number'
                      min='0'
                      value={formData.bathrooms}
                      onChange={(e) =>
                        setFormData({ ...formData, bathrooms: e.target.value })
                      }
                      placeholder='2'
                      className='mt-2'
                      data-testid='bathrooms-input'
                    />
                  </div>

                  <div>
                    <Label htmlFor='parking'>Parqueaderos</Label>
                    <Input
                      id='parking'
                      type='number'
                      min='0'
                      value={formData.parking}
                      onChange={(e) =>
                        setFormData({ ...formData, parking: e.target.value })
                      }
                      placeholder='1'
                      className='mt-2'
                      data-testid='parking-input'
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className='space-y-4 pt-6 border-t border-slate-200'>
                <h2 className='text-xl font-heading font-semibold text-slate-900'>
                  Ubicación
                </h2>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label htmlFor='department'>Departamento *</Label>
                    <Select
                      value={formData.department}
                      onValueChange={handleDepartmentChange}
                    >
                      <SelectTrigger
                        id='department'
                        className='mt-2'
                        data-testid='department-select'
                      >
                        <SelectValue placeholder='Seleccionar' />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept} value={dept}>
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor='city'>Ciudad *</Label>
                    <Select
                      value={formData.city}
                      onValueChange={(value) =>
                        setFormData({ ...formData, city: value })
                      }
                      disabled={!formData.department}
                    >
                      <SelectTrigger
                        id='city'
                        className='mt-2'
                        data-testid='city-select'
                      >
                        <SelectValue
                          placeholder={
                            formData.department
                              ? 'Seleccionar ciudad'
                              : 'Primero selecciona departamento'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor='neighborhood'>Barrio</Label>
                  <Input
                    id='neighborhood'
                    value={formData.neighborhood}
                    onChange={(e) =>
                      setFormData({ ...formData, neighborhood: e.target.value })
                    }
                    placeholder='Chapinero Alto'
                    className='mt-2'
                    data-testid='neighborhood-input'
                  />
                </div>

                <div>
                  <Label htmlFor='address_text'>Dirección</Label>
                  <Input
                    id='address_text'
                    value={formData.address_text}
                    onChange={(e) =>
                      setFormData({ ...formData, address_text: e.target.value })
                    }
                    placeholder='Calle 60 #10-20'
                    className='mt-2'
                    data-testid='address-input'
                  />
                </div>
              </div>

              {/* Images */}
              <div className='space-y-4 pt-6 border-t border-slate-200'>
                <h2 className='text-xl font-heading font-semibold text-slate-900'>
                  Imágenes *
                </h2>
                <p className='text-sm text-slate-500 -mt-2'>
                  Sube hasta 5 imágenes de tu propiedad (JPG, PNG, WebP, GIF -
                  máx 10MB cada una)
                </p>

                <ImageUploader
                  images={images}
                  onChange={setImages}
                  maxImages={5}
                />
              </div>

              {/* Submit */}
              <div className='pt-6'>
                <Button
                  type='submit'
                  className='w-full bg-primary hover:bg-primary-hover h-12 text-base font-semibold'
                  disabled={loading || images.length === 0}
                  data-testid='publish-submit'
                >
                  {loading ? 'Publicando...' : 'Publicar Propiedad'}
                </Button>
                <p className='text-sm text-slate-500 text-center mt-3'>
                  Tu propiedad será revisada por un administrador antes de ser
                  publicada
                </p>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PublishPropertyPage;
